import React, { forwardRef } from "react";
import { CircleNotchIcon } from "@phosphor-icons/react";
import styles from "./Button.module.css";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Visual treatment. Aurora-spec §8.1 met één toegevoegde variant:
   *
   *  - `destructiveGhost` — rode tekst, transparent achtergrond. Gebruik
   *    voor destructieve acties die in een ROW staan tussen andere row-
   *    actions (zoals "Verwijderen" in een gebruikerstabel). Houd
   *    `destructive` (solid rood) voor de CONFIRM-knop in een delete-
   *    confirmation-modal — daar past prominentie wel.
   */
  variant?:
    | "primary"
    | "secondary"
    | "ghost"
    | "icon"
    | "destructive"
    | "destructiveGhost";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
  icon?: React.ReactNode;
  children?: React.ReactNode;
}

const ICON_SIZE: Record<ButtonProps["size"] & string, number> = {
  sm: 16,
  md: 18,
  lg: 20,
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      loading = false,
      icon,
      children,
      className,
      disabled,
      ...rest
    },
    ref,
  ) => {
    const classNames = [
      styles.button,
      styles[variant],
      styles[size],
      loading ? styles.loading : "",
      className ?? "",
    ]
      .filter(Boolean)
      .join(" ");

    const iconSize = ICON_SIZE[size];

    return (
      <button
        ref={ref}
        className={classNames}
        disabled={disabled || loading}
        {...rest}
      >
        {loading ? (
          <CircleNotchIcon
            size={iconSize}
            weight="regular"
            className={styles.spinner}
            aria-hidden="true"
          />
        ) : icon ? (
          <span className={styles.iconWrapper} aria-hidden="true">
            {icon}
          </span>
        ) : null}

        {variant !== "icon" && children}
      </button>
    );
  },
);

Button.displayName = "Button";

export { Button };
export type { ButtonProps };
